import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { ApiError, resendVerificationRequest } from '@/auth/api';
import { withNext } from '@/auth/nextPath';
import { useAuth } from '@/auth/useAuth';
import { PrimaryButton } from '@/components/PrimaryButton';
import { reserveDeviceRequest } from '@/features/account/api';
import { rememberPendingReservation, reservationConditions } from '@/features/account/reservations';
import { formatHistoryTimestamp } from '@/features/operations/labels';
import { usePriceDisplay } from '@/features/operations/PriceDisplayProvider';

const RESERVATION_DAYS = 3;
/// `?reserve=1` on the sheet: the buyer came back from signing in to reserve this device.
const RESERVE_PARAM = 'reserve';

export type VerifyVerb = 'reservar' | 'comprar';

type Problem =
  { kind: 'unavailable' } | { kind: 'limit'; message: string } | { kind: 'failed' } | null;

/**
 * The "Reservar" of a public device sheet (VEN-3).
 *
 * - Without a session it takes the buyer to sign in, and back here with the dialog open.
 * - With the mail not verified it says so next to the button, with a way to get the link again,
 *   and remembers the device so the verification page can bring the buyer back.
 * - With a verified mail it opens the confirmation: conditions, price and end, full screen on a
 *   phone (UC-057). Confirming goes to the reservation's own page.
 *
 * Staff reserve from the panel (VEN-2), so for them the button only points there.
 */
export function WebReservationAction({
  deviceId,
  deviceName,
  priceMinor,
  secondaryAction,
  onUnavailable,
}: {
  deviceId: string;
  deviceName: string;
  priceMinor: number | null;
  /// Shown next to the button: the sheet's "Comprar" (VEN-7). As a function it gets a way to
  /// show the one verification notice under both buttons, full width, with its own verb.
  secondaryAction?: ReactNode | ((askToVerify: (verb: VerifyVerb) => void) => ReactNode);
  /// The device was taken while the dialog was open: the sheet reads it again on close.
  onUnavailable?: () => void;
}) {
  const { user, token, refreshUser } = useAuth();
  const { formatNullablePrice } = usePriceDisplay();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [open, setOpen] = useState(false);
  /// What the buyer tried to do before verifying the mail: the notice names it.
  const [verifyFor, setVerifyFor] = useState<VerifyVerb | null>(null);
  const [resendState, setResendState] = useState<'idle' | 'sending' | 'sent' | 'failed'>('idle');
  const [isChecking, setIsChecking] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [problem, setProblem] = useState<Problem>(null);
  const autoOpened = useRef(false);
  const sheetPath = `/catalog/${deviceId}`;
  const isStaff = Boolean(user && user.role !== 'CUSTOMER');
  const expiresAt = new Date(Date.now() + RESERVATION_DAYS * 24 * 60 * 60 * 1000);

  const start = async () => {
    setProblem(null);

    if (!user || !token) {
      navigate(withNext('/login', `${sheetPath}?${RESERVE_PARAM}=1`));
      return;
    }

    let verified = user.emailVerified;
    if (!verified) {
      // The mail may have been verified in another tab since this session started.
      setIsChecking(true);
      try {
        verified = Boolean((await refreshUser())?.emailVerified);
      } catch {
        verified = false;
      } finally {
        setIsChecking(false);
      }
    }

    if (!verified) {
      rememberPendingReservation(deviceId);
      setVerifyFor('reservar');
      return;
    }

    setVerifyFor(null);
    setOpen(true);
  };

  // Back from signing in with `?reserve=1`: open the confirmation once, and drop the flag so a
  // reload does not open it again.
  useEffect(() => {
    if (autoOpened.current || searchParams.get(RESERVE_PARAM) !== '1' || !user) return;
    autoOpened.current = true;
    const next = new URLSearchParams(searchParams);
    next.delete(RESERVE_PARAM);
    setSearchParams(next, { replace: true });
    if (user.role === 'CUSTOMER') void start();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, user]);

  const resend = async () => {
    if (!user) return;
    setResendState('sending');
    try {
      await resendVerificationRequest(user.email);
      setResendState('sent');
    } catch {
      setResendState('failed');
    }
  };

  const close = () => {
    if (isSaving) return;
    setOpen(false);
    if (problem?.kind === 'unavailable') onUnavailable?.();
  };

  const confirm = async () => {
    if (!token) return;
    setIsSaving(true);
    setProblem(null);
    try {
      const reservation = await reserveDeviceRequest(token, deviceId);
      navigate(`/account/reservations/${reservation.id}`, { state: { justReserved: true } });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        setProblem({ kind: 'unavailable' });
      } else if (error instanceof ApiError && error.status === 429) {
        setProblem({ kind: 'limit', message: error.message });
      } else if (error instanceof ApiError && error.details?.code === 'EMAIL_NOT_VERIFIED') {
        setOpen(false);
        rememberPendingReservation(deviceId);
        setVerifyFor('reservar');
      } else if (error instanceof ApiError && error.status === 401) {
        navigate(withNext('/login', `${sheetPath}?${RESERVE_PARAM}=1`));
      } else {
        setProblem({ kind: 'failed' });
      }
    } finally {
      setIsSaving(false);
    }
  };

  const secondary =
    typeof secondaryAction === 'function' ? secondaryAction(setVerifyFor) : secondaryAction;

  if (isStaff) {
    return (
      <Stack spacing={1} sx={{ width: '100%' }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
          <PrimaryButton disabled fullWidth>
            Reservar
          </PrimaryButton>
          {secondary}
        </Stack>
        <Typography variant="body2" color="text.secondary">
          El personal reserva desde la ficha del equipo en el panel.
        </Typography>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ width: '100%' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
        <PrimaryButton fullWidth disabled={isChecking} onClick={() => void start()}>
          {isChecking ? 'Verificando...' : 'Reservar'}
        </PrimaryButton>
        {secondary}
      </Stack>
      {verifyFor ? (
        <Alert severity="warning">
          Verificá tu email para {verifyFor}: te mandamos un enlace a {user?.email}. Cuando lo
          abras, volvés a este equipo.{' '}
          {resendState === 'sent' ? (
            <strong>Te enviamos un enlace nuevo.</strong>
          ) : (
            <Link
              component="button"
              type="button"
              onClick={() => void resend()}
              disabled={resendState === 'sending'}
              underline="always"
              sx={{ verticalAlign: 'baseline' }}
            >
              {resendState === 'sending' ? 'Enviando...' : 'Reenviar el enlace'}
            </Link>
          )}
          {resendState === 'failed'
            ? ' No pudimos enviarlo; volvé a intentar en un momento.'
            : null}
        </Alert>
      ) : null}

      <Dialog open={open} onClose={close} fullScreen={fullScreen} maxWidth="sm" fullWidth>
        <DialogTitle>Reservar {deviceName}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {problem?.kind === 'unavailable' ? (
              <Alert severity="error">
                Este equipo ya no está disponible: alguien lo reservó o lo compró recién.{' '}
                <Link component={RouterLink} to="/catalog" underline="always">
                  Ver otros equipos
                </Link>
              </Alert>
            ) : null}
            {problem?.kind === 'limit' ? (
              <Alert severity="warning">
                {problem.message}{' '}
                <Link component={RouterLink} to="/account" underline="always">
                  Ver mis reservas
                </Link>
              </Alert>
            ) : null}
            {problem?.kind === 'failed' ? (
              <Alert severity="error">No pudimos reservar el equipo. Volvé a intentar.</Alert>
            ) : null}
            <Typography>{reservationConditions}</Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2" color="text.secondary">
                Precio reservado
              </Typography>
              <Typography variant="h5" component="p">
                {formatNullablePrice(priceMinor)}
              </Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary">
              Vence el <strong>{formatHistoryTimestamp(expiresAt.toISOString())}</strong>
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" disabled={isSaving} onClick={close}>
            {problem?.kind === 'unavailable' ? 'Cerrar' : 'Cancelar'}
          </Button>
          {problem?.kind === 'unavailable' ? null : (
            <PrimaryButton disabled={isSaving} onClick={() => void confirm()}>
              {isSaving ? 'Reservando...' : 'Confirmar reserva'}
            </PrimaryButton>
          )}
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
